/**
 * Sticky Mobile Add-to-Cart
 * Shows a sticky add-to-cart bar on mobile when user scrolls past the original form
 */

class StickyMobileATC extends HTMLElement {
  constructor() {
    super();
    this.originalForm = null;
    this.stickyForm = null;
    this.observer = null;
    this.lastScrollY = 0;
    this.isVisible = false;
  }

  connectedCallback() {
    // Only run on mobile
    if (window.innerWidth >= 750) return;

    this.originalForm = document.querySelector('product-form-component');
    if (!this.originalForm) return;

    this.setupStickyBar();
    this.setupObserver();
    this.syncQuantity();
    this.setupEventListeners();

    // Set CSS variable for body padding
    this.setCSSVariable();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setCSSVariable() {
    const height = this.offsetHeight;
    document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: '-100px 0px 0px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.hide();
        } else {
          this.show();
        }
      });
    }, options);

    // Observe the original buy buttons section
    const buyButtons = this.originalForm.querySelector('.product-form-buttons');
    if (buyButtons) {
      this.observer.observe(buyButtons);
    }
  }

  setupEventListeners() {
    // Sync quantity changes
    const originalQuantityInput = this.originalForm?.querySelector('input[name="quantity"]');
    const stickyQuantityInput = this.querySelector('input[name="quantity"]');

    if (originalQuantityInput && stickyQuantityInput) {
      // Sync from original to sticky
      originalQuantityInput.addEventListener('input', (e) => {
        stickyQuantityInput.value = e.target.value;
      });

      // Sync from sticky to original
      stickyQuantityInput.addEventListener('input', (e) => {
        originalQuantityInput.value = e.target.value;
        // Trigger change event for any listeners
        originalQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    // Handle form submission from sticky bar
    const stickyAddToCartBtn = this.querySelector('.add-to-cart-button');
    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();

        // Sync quantity before submitting
        if (originalQuantityInput && stickyQuantityInput) {
          originalQuantityInput.value = stickyQuantityInput.value;
        }

        // Trigger the original form submission
        const originalSubmitBtn = this.originalForm?.querySelector('.add-to-cart-button');
        if (originalSubmitBtn) {
          originalSubmitBtn.click();
        }
      });
    }

    // Handle window resize
    this.handleResize = () => {
      if (window.innerWidth >= 750) {
        this.hide();
        if (this.observer) {
          this.observer.disconnect();
        }
      } else if (!this.observer) {
        this.setupObserver();
      }
      this.setCSSVariable();
    };
    window.addEventListener('resize', this.handleResize);

    // Update price and variant info when variant changes
    document.addEventListener('variant:change', (e) => {
      this.updateProductInfo(e.detail);
    });
  }

  syncQuantity() {
    const originalQuantityInput = this.originalForm?.querySelector('input[name="quantity"]');
    const stickyQuantityInput = this.querySelector('input[name="quantity"]');

    if (originalQuantityInput && stickyQuantityInput) {
      stickyQuantityInput.value = originalQuantityInput.value;
    }
  }

  updateProductInfo(variant) {
    if (!variant) return;

    // Update price
    const priceElement = this.querySelector('.sticky-mobile-atc__price');
    if (priceElement && variant.price) {
      const formattedPrice = new Intl.NumberFormat(document.documentElement.lang, {
        style: 'currency',
        currency: variant.price.currency_code || 'USD'
      }).format(variant.price.amount / 100);
      priceElement.textContent = formattedPrice;
    }

    // Update variant title
    const variantElement = this.querySelector('.sticky-mobile-atc__variant');
    if (variantElement && variant.title && variant.title !== 'Default Title') {
      variantElement.textContent = variant.title;
    }

    // Update availability
    const addToCartBtn = this.querySelector('.add-to-cart-button');
    if (addToCartBtn) {
      addToCartBtn.disabled = !variant.available;
      if (variant.available) {
        addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to cart';
      } else {
        addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'Sold out';
      }
    }
  }

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.classList.remove('hidden');
    this.classList.add('visible');
    this.setAttribute('aria-hidden', 'false');
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.classList.remove('visible');
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
  }

  setupStickyBar() {
    // Initial state is hidden
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('sticky-mobile-atc')) {
  customElements.define('sticky-mobile-atc', StickyMobileATC);
}
